import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import Button from "~/components/buttons/Button/Button";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import FormSection, { FormSectionGroup } from "~/components/formElements/FormSection";
import Input from "~/components/formElements/Input";
import RadioGroup from "~/components/formElements/RadioGroup/RadioGroup";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import AddressFormSection from "~/components/formSections/AddressFormSection/AddressFormSection.tsx";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { DEFAULT_COUNTRY_CODE, INVOICE_BY_MAIL_OPTIONS } from "~/constants/form.ts";
import useCountryCodeSelectOptions from "~/hooks/form/formOptionsData/useCountryCodeSelectOptions.ts";
import { useFormIsSubmittable } from "~/hooks/form/useFormIsSubmittable.ts";
import {
	createCentralInvoiceRecipient,
	updateCentralInvoiceRecipient,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientApiDispatchers.ts";
import type {
	CentralInvoiceRecipient,
	CreateOrUpdateCentralInvoiceRecipientData,
} from "~/modules/billing/api/centralInvoiceRecipient/centralInvoiceRecipientTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type Props = {
	clientId: string,
	initialValues?: CentralInvoiceRecipient
	onCancelClick: () => void;
	onSuccess: () => void;
};

type FormData = Omit<CreateOrUpdateCentralInvoiceRecipientData, "clientId" | "address"> & {
	addressLine1: string | null;
	addressLine2: string | null;
	addressLine3: string | null;
	city: string | null;
	postCode: string | null;
	countryCode: string | null;
};

const CreateOrUpdateCentralInvoiceRecipientForm = ({
	clientId,
	initialValues,
	onCancelClick,
	onSuccess,
}: Props) => {
	const [busy, setBusy] = useState(false);
	const isUpdateProcess = !!initialValues;
	const existingAddress = initialValues?.address;

	const schema = useMemo(() => {
		return yup.object({
			addressLine1: yup.string().default(""),
			addressLine2: yup.string().default(""),
			addressLine3: yup.string().default(""),
			city: isUpdateProcess ? yup.string().default("") : yup.string().required().default(""),
			comment: yup.string().default(""),
			countryCode: isUpdateProcess ? yup.string().default("") : yup.string().required().default(""),
			displayName: yup.string().required().default(""),
			invoiceEmail: yup.string().email().required().default(""),
			invoiceByMail: yup.boolean().required(),
			legalName: yup.string().required().default(""),
			postCode: isUpdateProcess ? yup.string().default("") : yup.string().required().default(""),
			supportContactName: yup.string().default(""),
			supportContactEmail: yup.string().default(""),
			supportContactPhone: yup.string().default(""),
		});
	}, [isUpdateProcess]);

	const defaultValues = useMemo(() => {
		return {
			addressLine1: existingAddress?.addressLine1 || "",
			addressLine2: existingAddress?.addressLine2 || "",
			addressLine3: existingAddress?.addressLine3 || "",
			city: existingAddress?.city || "",
			comment: initialValues?.comment || "",
			countryCode: existingAddress?.countryCode || DEFAULT_COUNTRY_CODE,
			displayName: initialValues?.displayName || "",
			invoiceEmail: initialValues?.invoiceEmail || "",
			invoiceByMail: initialValues?.invoiceByMail === undefined ? true : initialValues.invoiceByMail,
			legalName: initialValues?.legalName || "",
			postCode: existingAddress?.postCode || "",
			supportContactName: initialValues?.supportContactName || "",
			supportContactEmail: initialValues?.supportContactEmail || "",
			supportContactPhone: initialValues?.supportContactPhone || "",
		};
	}, [initialValues, existingAddress]);

	const {
		handleSubmit,
		formState: { isValid, isDirty, isSubmitted },
		control,
		trigger,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		mode: isUpdateProcess ? "onChange" : "onSubmit",
		resolver: yupResolver<FormData>(schema),
	});

	const formIsSubmittable = useFormIsSubmittable({
		isDirty,
		isSubmitted,
		isValid,
		isLoading: busy,
		enableInitially: !isUpdateProcess,
	});

	const countryCodeSelectOptions = useCountryCodeSelectOptions();

	useEffect(() => {
		if (isUpdateProcess) {
			trigger();
		}
	}, [isUpdateProcess, trigger]);

	const onSubmit = async (data: FormData) => {
		setBusy(true);
		const { addressLine1, addressLine2, addressLine3, city, postCode, countryCode, ...rest } = data;

		const submitData: CreateOrUpdateCentralInvoiceRecipientData = {
			...rest,
			address: { addressLine1, addressLine2, addressLine3, city, postCode, countryCode },
		};

		try {
			if (isUpdateProcess) {
				await updateCentralInvoiceRecipient({
					clientId,
					invoiceRecipientId: initialValues.id,
					data: submitData,
				});
			} else {
				await createCentralInvoiceRecipient({ clientId, data: submitData });
			}
			onSuccess();
		} catch (error) {
			console.log(error);
		}
	};

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			onKeyDown={preventSubmitOnEnter}
			className="flex min-h-full w-full flex-col justify-start"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>{initialValues ? "Rechnungsempfänger aktualisieren" : "Rechnungsempfänger erstellen"}</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{busy && <SidebarBusyOverlay />}
				<FormSectionGroup>
					<FormSection title="Name / Titel">
						<Input
							name="displayName"
							control={control}
						/>
					</FormSection>
					<FormSection title="Rechnungsempfänger">
						<Textarea
							rows={3}
							control={control}
							name="legalName"
							placeholder="Firma oder Rechnungskopf"
						/>
					</FormSection>

					<AddressFormSection<FormData>
						control={control}
						countryCodeSelectOptions={countryCodeSelectOptions}
						initialShowLine3={!!existingAddress?.addressLine3}
					/>

					<FormSection title="Rechnungsversand">
						<div className="space-y-4">
							<RadioGroup
								control={control}
								label="Versand per"
								name="invoiceByMail"
								options={INVOICE_BY_MAIL_OPTIONS}
								numCols={2}
							/>
							<Input
								name="invoiceEmail"
								control={control}
								label="E-Mail für Rechnungsversand"
							/>
						</div>
					</FormSection>
					<FormSection title="Support Kontakt">
						<div className="grid grid-cols-6 gap-6">
							<div className="col-span-6">
								<Input
									name="supportContactName"
									control={control}
									label="Name Kontaktperson"
								/>
							</div>
							<div className="col-span-3">
								<Input
									name="supportContactPhone"
									control={control}
									label="Telefon"
								/>
							</div>
							<div className="col-span-3">
								<Input
									name="supportContactEmail"
									control={control}
									label="E-Mail"
								/>
							</div>
						</div>
					</FormSection>

					<FormSection title="Anmerkungen">
						<Textarea
							control={control}
							name="comment"
						/>
					</FormSection>
				</FormSectionGroup>
			</SidebarContent>
			<SidebarFooter>
				<FormHasErrorsHint show={isUpdateProcess && !isValid || (!isUpdateProcess && !isValid && isSubmitted)}
								   className="mr-2" />
				<SubmitButton busy={busy}
							  disabled={!formIsSubmittable}>
					{t(normalizeTKey("form:buttonTexts.save"))}
				</SubmitButton>
				<Button theme="none"
						onClick={onCancelClick}>
					{t(normalizeTKey("form:buttonTexts.cancel"))}
				</Button>
			</SidebarFooter>
		</form>
	);
};

export default CreateOrUpdateCentralInvoiceRecipientForm;
